import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLogout, useMe } from '../auth/useMe';
import { endpoints } from '../api/endpoints';
import { ApiError } from '../api/client';
import type { Rol } from '../auth/types';

/**
 * Pantalla de rebote: llega quien entró bien con su cuenta pero **no tiene ningún
 * rol** en la app, o quien fue desactivado por un ADMIN. Desde acá pide el permiso.
 *
 * ## Por qué es un diálogo de la app y no una pantalla propia
 *
 * Hasta el 2026-09-01 esto usaba el mismo `LienzoMarca` que `/login`: mismo lienzo
 * oscuro, mismas cintas, misma tipografía display. La idea era que se leyera como la
 * misma pieza. **En la práctica hace lo contrario**: la persona pone su usuario y lo
 * que vuelve a ver es una pantalla igual a la que acaba de dejar. Sin leer, entiende
 * "el login falló, volví al principio" — y avisa que la app está rota.
 *
 * Ahora es **el diálogo que ya usa la app** (`components/Confirmacion.tsx`): medallón
 * redondo de color, título y cuerpo al lado, y las acciones abajo a la derecha con el
 * secundario con borde y el primario relleno. Centrado, sobre un fondo oscuro que
 * **no** es el del login.
 *
 * ## Sigue el tema de la app, y eso no es opcional
 *
 * La primera versión de la tarjeta fijaba la paleta clara con el argumento de que
 * "quien está acá no eligió tema" (el criterio de `LienzoMarca`). **Está mal**: el
 * tema vive en `localStorage`, no en la sesión, así que la clase `.dark` está puesta
 * aunque no haya sesión. Y `globals.css` trae un fallback de modo oscuro para los
 * controles de formulario —`.dark input, .dark select, .dark textarea`— cuya
 * especificidad (0-1-1) **le gana a un `bg-white` pelado** (0-1-0). Resultado: la
 * tarjeta salía blanca con los campos negros, ilegible. Cada superficie de esta
 * pantalla lleva su variante `dark:`.
 *
 * Reglas que sostienen la pieza:
 *
 * 1. **El cuerpo empieza diciendo que el ingreso funcionó**, en negrita: es la
 *    respuesta a la pregunta que la persona trae.
 * 2. **Siete estados, cada uno con su salida.** Ofrecer "cerrar sesión" a quien nunca
 *    inició una, o "pedir acceso" a quien ya tiene, era ruido.
 * 3. **El pedido sale de la identidad que verificó Microsoft**, no de lo que se
 *    escriba: nadie pide acceso por otro.
 */

/** Las siete situaciones desde las que se puede llegar a esta pantalla. */
type Estado =
  | 'verificando'
  | 'pedir'
  | 'ya_pidio'
  | 'desactivado'
  | 'con_acceso'
  | 'sin_ingreso'
  | 'falla';

/** Lo que puede necesitar hacer, dicho como lo diría quien no conoce la app. */
const QUE_NECESITO: Array<{ rol: Rol; texto: string }> = [
  { rol: 'SOLICITANTE', texto: 'Pedir compras' },
  { rol: 'AUTORIZADOR', texto: 'Aprobar o rechazar compras' },
  { rol: 'ADMINISTRACION', texto: 'Armar la orden de pre pago y registrar pagos' },
  { rol: 'RECEPCION', texto: 'Recibir mercadería' },
];

// Los mismos tokens que `Confirmacion.tsx` y los modales de la app, con sus dos
// variantes. El deshabilitado va en gris y no en el azul al 50%: un primario
// desteñido se lee como un botón roto, no como "todavía no".
const BOTON_SECUNDARIO =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';
const BOTON_PRIMARIO =
  'inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white transition hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500';
const CAMPO =
  'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-accent focus:ring-1 focus:ring-accent dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500';
const ETIQUETA = 'mb-1 block text-sm text-slate-600 dark:text-slate-300';
const AYUDA = 'mt-1 block text-xs leading-snug text-slate-500 dark:text-slate-400';

export default function SinAcceso() {
  const [params] = useSearchParams();
  const desactivado = params.get('motivo') === 'desactivado';
  const logout = useLogout();

  const [motivo, setMotivo] = useState('');
  const [rolSugerido, setRolSugerido] = useState<Rol | ''>('');
  const [codigoFlexxus, setCodigoFlexxus] = useState('');

  /**
   * Quién es el que está mirando la pantalla. Responde 401 si llegó a la URL sin
   * pasar por el login: ahí no se ofrece pedir nada, porque no habría a nombre de
   * quién hacerlo.
   */
  const pendiente = useQuery({
    queryKey: ['pedido-acceso'],
    queryFn: endpoints.estadoPedidoAcceso,
    retry: false,
    enabled: !desactivado,
  });

  /**
   * Solo para distinguir "no hay nadie" de "hay alguien y sí tiene acceso".
   *
   * Ojo con los roles: `/api/me` responde 200 con la lista **vacía** cuando la
   * sesión sigue viva y le revocaron los permisos. Mirar solo `me.data` daba
   * "Ya podés entrar" justo a quien acababa de perder el acceso, y el botón lo
   * devolvía a `/`, que lo mandaba de nuevo acá: un bucle cerrado sin manera de
   * pedir el permiso.
   */
  const me = useMe();
  const tieneAcceso = (me.data?.roles?.length ?? 0) > 0;

  const pedir = useMutation({
    mutationFn: () =>
      endpoints.pedirAcceso({
        motivo,
        rolSugerido: rolSugerido || null,
        codigoFlexxus: codigoFlexxus.trim() || null,
      }),
    onSuccess: () => pendiente.refetch(),
  });

  const identidad = pendiente.data;
  const error = pendiente.error;
  const sinIngresoPendiente = error instanceof ApiError && error.status === 401;
  const fallaReal = Boolean(error) && !sinIngresoPendiente;

  /** Un solo lugar decide qué se muestra. */
  const estado: Estado = desactivado
    ? 'desactivado'
    : pendiente.isLoading || (sinIngresoPendiente && me.isLoading)
      ? 'verificando'
      : fallaReal
        ? 'falla'
        : identidad
          ? identidad.pedidoEn || pedir.isSuccess
            ? 'ya_pidio'
            : 'pedir'
          : tieneAcceso
            ? 'con_acceso'
            : 'sin_ingreso';

  const texto = TEXTOS[estado]({
    nombre: (identidad?.nombre ?? me.data?.nombre ?? '').split(' ')[0] ?? '',
    buzon: identidad?.buzon ?? null,
    motivoFalla: fallaReal ? (error as Error).message : '',
  });
  const medallon = MEDALLONES[texto.tono];

  return (
    // Fondo propio, deliberadamente distinto al del login: solo la luz de marca, sin
    // cintas ni malla isométrica. `items-center` + `my-auto` centra la tarjeta y deja
    // el scroll llegar arriba cuando el formulario no entra en pantallas bajas.
    <main className="flex min-h-dvh items-center justify-center overflow-y-auto bg-[#061a2c] p-4">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 bg-[image:radial-gradient(40rem_28rem_at_12%_-10%,#0e4f7c_0%,transparent_62%),radial-gradient(36rem_26rem_at_92%_110%,#11533a_0%,transparent_58%)]"
      />

      <section
        role="dialog"
        aria-labelledby="sin-acceso-titulo"
        className="relative my-auto w-full max-w-lg animate-entrada rounded-lg bg-white p-6 shadow-2xl ring-1 ring-slate-900/5 motion-reduce:animate-none dark:bg-slate-900 dark:ring-white/10"
      >
        <div className="flex items-start gap-3">
          <div
            className={`grid h-10 w-10 flex-none place-items-center rounded-full ${medallon.clase}`}
          >
            <i aria-hidden="true" className={`ph ${medallon.icono} text-lg`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              id="sin-acceso-titulo"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              {texto.titulo}
            </h3>
            <p
              className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
              aria-live="polite"
            >
              {texto.destacado && (
                <strong className="font-semibold text-slate-900 dark:text-white">
                  {texto.destacado}{' '}
                </strong>
              )}
              {texto.cuerpo}
            </p>
          </div>
        </div>

        {/* El correo lo pone el login: es la prueba de que la cuenta entró. */}
        {identidad && (
          <p className="mt-4 flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
            <i
              aria-hidden="true"
              className="ph ph-user-circle text-base text-slate-400 dark:text-slate-500"
            />
            <span className="min-w-0 break-all">
              Entraste como{' '}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {identidad.email}
              </span>
            </span>
          </p>
        )}

        {estado === 'pedir' && (
          <form
            className="mt-5 space-y-4 border-t border-slate-200 pt-5 dark:border-slate-800"
            onSubmit={(e) => {
              e.preventDefault();
              if (motivo.trim()) pedir.mutate();
            }}
          >
            <label className="block">
              <span className={ETIQUETA}>¿Qué necesitás hacer?</span>
              <select
                value={rolSugerido}
                onChange={(e) => setRolSugerido(e.target.value as Rol | '')}
                className={CAMPO}
              >
                <option value="">No sé / otra cosa</option>
                {QUE_NECESITO.map((o) => (
                  <option key={o.rol} value={o.rol}>
                    {o.texto}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className={ETIQUETA}>Contanos para qué *</span>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={3}
                maxLength={500}
                required
                placeholder="Soy del área comercial y necesito cargar las compras de mis clientes."
                className={`${CAMPO} resize-none`}
              />
              {/* El botón arranca deshabilitado: sin este aviso, parece roto. */}
              <span className={AYUDA}>Con esto alcanza. Es lo único obligatorio.</span>
            </label>

            <label className="block">
              <span className={ETIQUETA}>
                Tu usuario de Flexxus{' '}
                <span className="text-slate-500 dark:text-slate-400">(si lo sabés)</span>
              </span>
              <input
                value={codigoFlexxus}
                onChange={(e) => setCodigoFlexxus(e.target.value)}
                maxLength={15}
                placeholder="Opcional"
                className={`${CAMPO} uppercase placeholder:normal-case`}
              />
              {/* Lo verifica y lo carga un ADMIN: si se autoasignara, cualquiera
                  podría operar en el ERP con la identidad de un compañero. */}
              <span className={AYUDA}>
                Lo revisa un administrador antes de vincularlo. Si no lo sabés, dejalo vacío.
              </span>
            </label>

            {pedir.isError && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/15 dark:text-red-300">
                {(pedir.error as Error).message}
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <BotonSalir logout={logout} />
              <button
                type="submit"
                disabled={pedir.isPending || !motivo.trim()}
                className={BOTON_PRIMARIO}
              >
                <i aria-hidden="true" className="ph ph-paper-plane-tilt text-base opacity-80" />
                {pedir.isPending ? 'Enviando…' : 'Enviar mi pedido'}
              </button>
            </div>
          </form>
        )}

        {/* Cada salida es la que sirve en ese estado. El secundario va primero y el
            primario último, como en el resto de los diálogos de la app. */}
        {estado !== 'pedir' && (
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            {estado === 'con_acceso' && (
              <>
                <BotonSalir logout={logout} />
                <Link to="/" className={BOTON_PRIMARIO}>
                  <i aria-hidden="true" className="ph ph-arrow-right text-base opacity-80" />
                  Entrar a la app
                </Link>
              </>
            )}

            {estado === 'sin_ingreso' && (
              <Link to="/login" className={BOTON_PRIMARIO}>
                <i aria-hidden="true" className="ph ph-sign-in text-base opacity-80" />
                Iniciar sesión
              </Link>
            )}

            {estado === 'falla' && (
              <>
                <BotonSalir logout={logout} />
                <button
                  type="button"
                  onClick={() => pendiente.refetch()}
                  disabled={pendiente.isFetching}
                  className={BOTON_PRIMARIO}
                >
                  <i aria-hidden="true" className="ph ph-arrow-clockwise text-base opacity-80" />
                  {pendiente.isFetching ? 'Probando…' : 'Probar de nuevo'}
                </button>
              </>
            )}

            {(estado === 'ya_pidio' || estado === 'desactivado') && <BotonSalir logout={logout} />}
          </div>
        )}
      </section>
    </main>
  );
}

type Tono = 'ok' | 'pendiente' | 'problema' | 'neutro';

/**
 * El medallón del diálogo de la app, con las dos variantes. Lleva ícono además de
 * color: el estado no puede depender solo del color.
 */
const MEDALLONES: Record<Tono, { clase: string; icono: string }> = {
  ok: {
    clase: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400',
    icono: 'ph-check',
  },
  pendiente: {
    clase: 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400',
    icono: 'ph-paper-plane-tilt',
  },
  problema: {
    clase: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400',
    icono: 'ph-warning',
  },
  neutro: {
    clase: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
    icono: 'ph-spinner',
  },
};

type DatosTexto = { nombre: string; buzon: string | null; motivoFalla: string };
type Copy = { tono: Tono; titulo: string; destacado?: string; cuerpo: string };

/**
 * El texto de cada estado. El "sin rol" no habla de grupos de seguridad: desde el
 * 2026-08-18 los permisos se otorgan con los roles de aplicación de Entra, y lo que
 * la persona necesita saber es que su ingreso anduvo y a quién pedirle el rol.
 *
 * `destacado` es la primera oración, en negrita: cuando el ingreso funcionó, eso es
 * lo único que hay que leer para no pensar que la app falló.
 */
const TEXTOS: Record<Estado, (d: DatosTexto) => Copy> = {
  verificando: () => ({
    tono: 'neutro',
    titulo: 'Estamos revisando tu ingreso',
    cuerpo: 'Tarda un segundo.',
  }),
  pedir: (d) => ({
    tono: 'ok',
    titulo: `${d.nombre ? `${d.nombre}, te` : 'Te'} falta un permiso para entrar`,
    destacado: 'Tu ingreso funcionó: no falló nada.',
    cuerpo:
      'Lo que falta es que alguien te asigne un rol en la App de Compras. Contanos qué necesitás hacer y le avisamos a quien puede dártelo.',
  }),
  ya_pidio: (d) => ({
    tono: 'pendiente',
    titulo: 'Ya avisamos: falta que te asignen el rol',
    destacado: 'Tu pedido quedó registrado.',
    cuerpo: `${
      d.buzon ? `Le llegó a ${d.buzon}. ` : ''
    }No hace falta mandarlo de nuevo: cuando te asignen el permiso, volvé a entrar y vas a pasar derecho.`,
  }),
  desactivado: () => ({
    tono: 'problema',
    titulo: 'Tu usuario está dado de baja',
    cuerpo:
      'Un administrador desactivó tu usuario en la App de Compras, así que el acceso quedó cortado. Si creés que es un error, pedile que te habilite de nuevo.',
  }),
  con_acceso: (d) => ({
    tono: 'ok',
    titulo: `${d.nombre ? `${d.nombre}, ya` : 'Ya'} podés entrar`,
    destacado: 'Tu cuenta tiene acceso.',
    cuerpo:
      'Esta ventana es para quien todavía no tiene permisos, y aparece sola cuando hace falta: no hay que buscarla.',
  }),
  sin_ingreso: () => ({
    tono: 'neutro',
    titulo: 'Primero hay que iniciar sesión',
    cuerpo:
      'Esta ventana se abre sola cuando alguien entra con su cuenta de Procom y todavía no tiene permisos en la App de Compras: desde ahí los pide. Como llegaste escribiendo la dirección, no hay ningún ingreso que revisar.',
  }),
  falla: (d) => ({
    tono: 'problema',
    titulo: 'No se pudo comprobar tu ingreso',
    cuerpo: `Algo falló al consultar el servidor${
      d.motivoFalla ? `: ${d.motivoFalla}` : ''
    }. Puede ser la conexión. Probá de nuevo en un momento.`,
  }),
};

/** Salida discreta: es la acción secundaria en todos los estados que la tienen. */
function BotonSalir({ logout }: { logout: ReturnType<typeof useLogout> }) {
  return (
    <button
      type="button"
      onClick={() => logout.mutate()}
      disabled={logout.isPending}
      className={BOTON_SECUNDARIO}
    >
      <i aria-hidden="true" className="ph ph-sign-out text-base opacity-70" />
      {logout.isPending ? 'Cerrando sesión…' : 'Cerrar sesión'}
    </button>
  );
}
